import { TriangleAlert, Hash, Target, Users } from "lucide-react";
import { formatScore, scoreColor } from "./analysisLabels";

const RankedList = ({ title, icon: Icon, tone, entries, empty, onSelect }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
    <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
      <Icon className={`h-4 w-4 ${tone}`} />
      {title}
    </p>
    {entries.length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">{empty}</p>
    ) : (
      <ul className="space-y-1">
        {entries.map((entry) => (
          <li key={entry.label}>
            <button
              onClick={() => onSelect?.(entry)}
              className="flex w-full items-start justify-between gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              title={
                entry.examples?.length
                  ? `Ex.: ${entry.examples.join(" / ")}`
                  : "Ver as conversas por trás deste número"
              }
            >
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {entry.label}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {entry.critical > 0 && (
                  <span
                    className="rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300"
                    title={`${entry.critical} em conversas críticas`}
                  >
                    {entry.critical} crítica{entry.critical > 1 ? "s" : ""}
                  </span>
                )}
                <span
                  className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  title={`${entry.count} conversas`}
                >
                  {entry.count}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const FocusPanel = ({ focus, agents, onSelect }) => (
  <div className="space-y-4">
    <RankedList
      title="Sinais de insatisfação mais frequentes"
      icon={TriangleAlert}
      tone="text-red-500"
      entries={focus.signals}
      empty="Nenhum sinal de insatisfação recorrente no período."
      onSelect={onSelect}
    />

    <RankedList
      title="Assuntos que mais aparecem"
      icon={Hash}
      tone="text-blue-500"
      entries={focus.topics}
      empty="Sem assuntos identificados no período."
      onSelect={onSelect}
    />

    <RankedList
      title="Ações recomendadas pela IA"
      icon={Target}
      tone="text-green-500"
      entries={focus.actions}
      empty="Nenhuma ação recomendada no período."
      onSelect={onSelect}
    />

    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Users className="h-4 w-4 text-purple-500" />
        Desempenho por atendente
      </p>
      {agents.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nenhum atendente identificado nas análises.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="pb-2 text-left font-medium">Atendente</th>
                <th className="pb-2 text-right font-medium">Conv.</th>
                <th className="pb-2 text-right font-medium">Nota</th>
                <th className="pb-2 text-right font-medium">Insat.</th>
                <th className="pb-2 text-right font-medium">Críticas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {agents.map((agent) => (
                <tr key={agent.name}>
                  <td className="py-2 pr-2 text-gray-900 dark:text-gray-100">
                    {agent.name}
                  </td>
                  <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                    {agent.conversations}
                  </td>
                  <td className={`py-2 text-right font-semibold ${scoreColor(agent.avgScore)}`}>
                    {formatScore(agent.avgScore)}
                  </td>
                  <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                    {agent.dissatisfied}
                  </td>
                  <td className="py-2 text-right font-medium text-red-600 dark:text-red-400">
                    {agent.critical || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Nota média atribuída pela IA à conduta do atendimento. Conversas sem
            atendente identificado na transcrição são creditadas aos responsáveis
            pela conversa no CRM.
          </p>
        </div>
      )}
    </div>
  </div>
);

export default FocusPanel;
